import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedDateInput, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { getTokenUsageReport } from '../services/tokenUsageService';
import type {
  TokenUsageBreakdownItem,
  TokenUsageGranularity,
  TokenUsageReport as TokenUsageReportModel,
} from '../types/tokenUsage';
import {
  buildCsv,
  calendarDateInTimeZone,
  downloadCsv,
  formatBucketRangeLabel,
  formatNumber,
  formatPercent,
  formatWindowRangeLabel,
  isValidTimeZone,
  parseLocalDate,
  resolveUiLocale,
  toDateInputValue,
} from '../utils/tokenUsageFormat';
import { TokenUsageChart } from './TokenUsageChart';
import styles from './AiUsage.module.css';

type PresetId = 'last7' | 'last30' | 'thisMonth' | 'last12Months';

type Preset = {
  id: PresetId;
  resolveRange: (today: Date) => { from: string; to: string };
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Grouping follows the range length instead of a manual control, so a long interval never renders
 * as hundreds of daily bars: up to ~6 weeks daily, up to ~6 months weekly, longer than that monthly.
 */
const resolveGranularity = (from: string, to: string): TokenUsageGranularity => {
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);

  if (!start || !end) {
    return 'day';
  }

  const days = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;

  if (days <= 45) {
    return 'day';
  }

  return days <= 186 ? 'week' : 'month';
};

const toLocalIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const PRESETS: Preset[] = [
  {
    id: 'last7',
    resolveRange: (today) => ({ from: toLocalIsoDate(shiftDays(today, -6)), to: toLocalIsoDate(today) }),
  },
  {
    id: 'last30',
    resolveRange: (today) => ({ from: toLocalIsoDate(shiftDays(today, -29)), to: toLocalIsoDate(today) }),
  },
  {
    id: 'thisMonth',
    resolveRange: (today) => ({
      from: toLocalIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: toLocalIsoDate(today),
    }),
  },
  {
    id: 'last12Months',
    resolveRange: (today) => ({
      from: toLocalIsoDate(new Date(today.getFullYear(), today.getMonth() - 11, 1)),
      to: toLocalIsoDate(today),
    }),
  },
];

export const TokenUsageReport: React.FC = () => {
  const { t, i18n } = useTranslation(['aiUsage', 'common', 'calendar']);
  const locale = resolveUiLocale(i18n.resolvedLanguage || i18n.language);

  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [modelName, setModelName] = React.useState('');
  const [modelNameQuery, setModelNameQuery] = React.useState('');
  const [triggeredBy, setTriggeredBy] = React.useState('all');
  const [status, setStatus] = React.useState('all');
  const [showFilters, setShowFilters] = React.useState(false);
  const [tenantTimeZone, setTenantTimeZone] = React.useState('');

  const [report, setReport] = React.useState<TokenUsageReportModel | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);

  const granularity = React.useMemo(() => resolveGranularity(from, to), [from, to]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setModelNameQuery(modelName.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [modelName]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);

    void getTokenUsageReport({
      granularity,
      from: from || undefined,
      to: to || undefined,
      modelName: modelNameQuery || undefined,
      triggeredBy: triggeredBy !== 'all' ? triggeredBy : undefined,
      status: status !== 'all' ? status : undefined,
    })
      .then((next) => {
        if (!active) {
          return;
        }

        setReport(next);
        if (next.window.timeZone) {
          setTenantTimeZone(next.window.timeZone);
        }
        setError(null);
      })
      .catch((loadError) => {
        if (!active) {
          return;
        }

        setReport(null);
        setError(loadError instanceof Error ? loadError.message : t('loadFailed'));
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [granularity, from, to, modelNameQuery, triggeredBy, status, reloadToken, t]);

  const todayInTenant = React.useMemo(
    () => calendarDateInTimeZone(tenantTimeZone),
    [tenantTimeZone],
  );

  const setFromDate = (next: string) => {
    setFrom(next);
    if (next && to && next > to) {
      setTo(next);
    }
  };

  const setToDate = (next: string) => {
    setTo(next);
    if (next && from && next < from) {
      setFrom(next);
    }
  };

  const applyPreset = (preset: Preset) => {
    const range = preset.resolveRange(todayInTenant);
    setFrom(range.from);
    setTo(range.to);
  };

  const activePresetId = React.useMemo<PresetId | null>(() => {
    if (!from || !to) {
      return null;
    }

    const match = PRESETS.find((preset) => {
      const range = preset.resolveRange(todayInTenant);
      return range.from === from && range.to === to;
    });

    return match?.id ?? null;
  }, [from, to, todayInTenant]);

  const resetFilters = () => {
    setFrom('');
    setTo('');
    setModelName('');
    setModelNameQuery('');
    setTriggeredBy('all');
    setStatus('all');
  };

  const hasCustomFilters = Boolean(from || to || modelName.trim() || triggeredBy !== 'all' || status !== 'all');

  const exportCsv = () => {
    if (!report) {
      return;
    }

    const csv = buildCsv(
      [
        t('columnPeriod'),
        t('columnStart'),
        t('columnEnd'),
        t('inputTokens'),
        t('outputTokens'),
        t('totalTokens'),
        t('completions'),
        t('operations'),
      ],
      report.series.map((point) => [
        point.key,
        toDateInputValue(point.fromLocal),
        toDateInputValue(point.toLocal),
        point.inputTokens,
        point.outputTokens,
        point.totalTokens,
        point.completionCount,
        point.operationCount,
      ]),
    );

    downloadCsv(
      `token-usage-${granularity}-${toDateInputValue(report.window.fromLocal)}-${toDateInputValue(report.window.toLocal)}.csv`,
      csv,
    );
  };

  const totals = report?.totals;
  const averagePerCompletion = totals && totals.completionCount > 0
    ? Math.round(totals.totalTokens / totals.completionCount)
    : 0;

  const formatLabel = (label: string): string => (label === 'unknown' ? t('unknownLabel') : label);

  const renderBreakdown = (title: string, items: TokenUsageBreakdownItem[]) => (
    <section className={styles.breakdownCard}>
      <h4 className={styles.breakdownTitle}>{title}</h4>
      {items.length === 0 ? (
        <p className={styles.breakdownEmpty}>{t('noUsageInRange')}</p>
      ) : (
        <ul className={styles.breakdownList}>
          {items.map((item) => {
            const share = totals && totals.totalTokens > 0 ? item.totalTokens / totals.totalTokens : 0;
            const label = formatLabel(item.label);

            return (
              <li key={item.label} className={styles.breakdownItem}>
                <div className={styles.breakdownItemHead}>
                  <span className={styles.breakdownLabel} title={label}>{label}</span>
                  <span className={styles.breakdownValue}>{formatNumber(item.totalTokens, locale)}</span>
                </div>
                <div className={styles.breakdownBar}>
                  <div className={styles.breakdownBarFill} style={{ width: `${Math.max(share * 100, 1)}%` }} />
                </div>
                <div className={styles.breakdownMeta}>
                  <span>{formatPercent(item.totalTokens, totals?.totalTokens ?? 0, locale)}</span>
                  <span>{t('completionsWithCount', { count: item.completionCount })}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  return (
    <div className={styles.report}>
      <section className={styles.controls} aria-label={t('filtersTitle')}>
        <div className={styles.controlsRow}>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>{t('quickRanges')}</span>
            <div className={styles.presets}>
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`${styles.presetBtn} ${activePresetId === preset.id ? styles.presetBtnActive : ''}`}
                  onClick={() => applyPreset(preset)}
                >
                  {t(`preset_${preset.id}`)}
                </button>
              ))}
            </div>
          </div>

          <label className={styles.controlGroup}>
            <span className={styles.controlLabel}>{t('from')}</span>
            <LocalizedDateInput
              className={styles.dateTrigger}
              value={from}
              onValueChange={setFromDate}
              emptyLabel={t('selectDate', { ns: 'calendar' })}
              aria-label={t('from')}
              fillWidth
            />
          </label>

          <label className={styles.controlGroup}>
            <span className={styles.controlLabel}>{t('to')}</span>
            <LocalizedDateInput
              className={styles.dateTrigger}
              value={to}
              onValueChange={setToDate}
              emptyLabel={t('selectDate', { ns: 'calendar' })}
              aria-label={t('to')}
              fillWidth
            />
          </label>
        </div>

        <div className={styles.controlsFooter}>
          <button
            type="button"
            className={styles.linkBtn}
            aria-expanded={showFilters}
            onClick={() => setShowFilters((current) => !current)}
          >
            {showFilters ? t('hideMoreFilters') : t('showMoreFilters')}
          </button>
          <div className={styles.controlsActions}>
            {hasCustomFilters ? (
              <button type="button" className="btn btn-secondary" onClick={resetFilters}>
                {t('reset')}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={loading || !report || report.series.length === 0}
              onClick={exportCsv}
            >
              {t('exportCsv')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={loading}
              onClick={() => setReloadToken((current) => current + 1)}
            >
              {loading ? t('loading', { ns: 'common' }) : t('refresh')}
            </button>
          </div>
        </div>

        {showFilters ? (
          <div className={styles.controlsRow}>
            <label className={styles.controlGroup}>
              <span className={styles.controlLabel}>{t('model')}</span>
              <input
                className={styles.controlInput}
                value={modelName}
                placeholder={t('modelPlaceholder')}
                onChange={(event) => setModelName(event.target.value)}
              />
            </label>
            <label className={styles.controlGroup}>
              <span className={styles.controlLabel}>{t('triggeredBy')}</span>
              <select
                className={styles.controlInput}
                value={triggeredBy}
                onChange={(event) => setTriggeredBy(event.target.value)}
              >
                <option value="all">{t('allSources')}</option>
                <option value="ai-assistant">{t('sourceAiAssistant')}</option>
                <option value="employee">{t('sourceEmployee')}</option>
                <option value="system">{t('sourceSystem')}</option>
              </select>
            </label>
            <label className={styles.controlGroup}>
              <span className={styles.controlLabel}>{t('status')}</span>
              <select
                className={styles.controlInput}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">{t('allStatuses')}</option>
                <option value="Succeeded">{t('statusSucceeded')}</option>
                <option value="Failed">{t('statusFailed')}</option>
              </select>
            </label>
          </div>
        ) : null}
      </section>

      {loading && !report ? (
        <div className={styles.state}>
          <Spinner text={t('loadingReport')} />
        </div>
      ) : error ? (
        <div className={`${styles.state} ${styles.stateError}`}>{error}</div>
      ) : report && totals ? (
        <>
          <div className={styles.windowBar}>
            <span className={styles.windowRange}>
              {formatWindowRangeLabel(report.window.fromLocal, report.window.toLocal, locale)}
            </span>
            <span className={styles.windowMeta}>{t(`granularity_${report.window.granularity}`)}</span>
            {report.window.timeZone ? (
              <span className={styles.windowMeta}>{report.window.timeZone}</span>
            ) : null}
            <span className={styles.windowMeta}>
              {t('generatedAt', {
                value: formatDateTime(
                  report.window.generatedAtUtc,
                  isValidTimeZone(report.window.timeZone) ? { timeZone: report.window.timeZone } : undefined,
                ),
              })}
            </span>
          </div>

          <div className={styles.statGrid}>
            <article className={`${styles.statCard} ${styles.statCardPrimary}`}>
              <span className={styles.statLabel}>{t('totalTokens')}</span>
              <strong className={styles.statValue}>{formatNumber(totals.totalTokens, locale)}</strong>
              <span className={styles.statHint}>
                {t('splitInputOutput', {
                  input: formatNumber(totals.inputTokens, locale),
                  output: formatNumber(totals.outputTokens, locale),
                })}
              </span>
            </article>
            <article className={styles.statCard}>
              <span className={styles.statLabel}>{t('completions')}</span>
              <strong className={styles.statValue}>{formatNumber(totals.completionCount, locale)}</strong>
              <span className={styles.statHint}>{t('completionsHint')}</span>
            </article>
            <article className={styles.statCard}>
              <span className={styles.statLabel}>{t('avgPerCompletion')}</span>
              <strong className={styles.statValue}>{formatNumber(averagePerCompletion, locale)}</strong>
              <span className={styles.statHint}>{t('avgPerCompletionHint')}</span>
            </article>
            <article className={styles.statCard}>
              <span className={styles.statLabel}>{t('conversations')}</span>
              <strong className={styles.statValue}>{formatNumber(totals.conversationCount, locale)}</strong>
              <span className={styles.statHint}>{t('runsHint', { count: totals.runCount })}</span>
            </article>
          </div>

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{t('usageOverTime')}</h3>
              <p className={styles.panelHint}>{t('usageOverTimeHint')}</p>
            </div>
            <TokenUsageChart
              series={report.series}
              granularity={report.window.granularity}
              locale={locale}
              labels={{
                inputTokens: t('inputTokens'),
                outputTokens: t('outputTokens'),
                totalTokens: t('totalTokens'),
                completions: t('completions'),
                empty: t('noUsageInRange'),
              }}
            />
          </section>

          {renderBreakdown(t('byModel'), report.breakdowns.byModel)}

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{t('breakdownByPeriod')}</h3>
              <p className={styles.panelHint}>{t('breakdownByPeriodHint')}</p>
            </div>
            {report.series.length === 0 ? (
              <div className={styles.state}>{t('noUsageInRange')}</div>
            ) : (
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">{t('columnPeriod')}</th>
                      <th scope="col" className={styles.tableNumeric}>{t('inputTokens')}</th>
                      <th scope="col" className={styles.tableNumeric}>{t('outputTokens')}</th>
                      <th scope="col" className={styles.tableNumeric}>{t('totalTokens')}</th>
                      <th scope="col" className={styles.tableNumeric}>{t('completions')}</th>
                      <th scope="col" className={styles.tableNumeric}>{t('avgPerCompletion')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.series.map((point) => (
                      <tr key={point.key} className={point.totalTokens === 0 ? styles.tableRowEmpty : undefined}>
                        <th scope="row" className={styles.tablePeriod}>
                          {formatBucketRangeLabel(point, report.window.granularity, locale)}
                        </th>
                        <td className={styles.tableNumeric}>{formatNumber(point.inputTokens, locale)}</td>
                        <td className={styles.tableNumeric}>{formatNumber(point.outputTokens, locale)}</td>
                        <td className={`${styles.tableNumeric} ${styles.tableStrong}`}>
                          {formatNumber(point.totalTokens, locale)}
                        </td>
                        <td className={styles.tableNumeric}>{formatNumber(point.completionCount, locale)}</td>
                        <td className={styles.tableNumeric}>
                          {point.completionCount > 0
                            ? formatNumber(Math.round(point.totalTokens / point.completionCount), locale)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">{t('total')}</th>
                      <td className={styles.tableNumeric}>{formatNumber(totals.inputTokens, locale)}</td>
                      <td className={styles.tableNumeric}>{formatNumber(totals.outputTokens, locale)}</td>
                      <td className={`${styles.tableNumeric} ${styles.tableStrong}`}>
                        {formatNumber(totals.totalTokens, locale)}
                      </td>
                      <td className={styles.tableNumeric}>{formatNumber(totals.completionCount, locale)}</td>
                      <td className={styles.tableNumeric}>
                        {averagePerCompletion > 0 ? formatNumber(averagePerCompletion, locale) : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};
